import { useEffect, useState } from 'react';
import '../pages/Admin.css';

const API_BASE = '/api';

type TabType = 'consultants' | 'companies' | 'sites' | 'community';

interface User {
  id: string;
  email: string;
  phone?: string;
  role: 'professional' | 'company' | 'site';
  status: string;
  verified?: boolean;
  approvalStatus?: 'pending' | 'approved' | 'verified';
  approvalDate?: string;
  verificationDate?: string;
  createdAt: string;
  profile?: {
    name: string;
    headline?: string;
    location?: string;
  };
}

interface CommunityPost {
  id: string;
  userId: string;
  userName: string;
  content: string;
  image?: string;
  createdAt: string;
  comments: Array<{
    id: string;
    userId: string;
    userName: string;
    content: string;
    createdAt: string;
  }>;
}

interface AdminProps {
  userRole?: string;
  onLoginSuccess?: (role: string) => void;
}

export default function AdminNew({ userRole, onLoginSuccess }: AdminProps) {
  const [activeTab, setActiveTab] = useState<TabType>('consultants');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // For all user tabs
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // For community
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);

  // Email prompt modal states
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [emailPromptType, setEmailPromptType] = useState<'approve' | 'verify' | null>(null);
  const [emailPromptUser, setEmailPromptUser] = useState<User | null>(null);
  const [emailTemplate, setEmailTemplate] = useState('');

  // Check if user is admin
  const isAuthorized = userRole === 'admin';

  // Handle admin login
  async function handleAdminLogin(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setIsLoggingIn(true);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailOrPhone: loginEmail, password: loginPassword }),
      });

      const data = await response.json();

      if (data.ok && data.role === 'admin') {
        // Store credentials
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('userId', data.userId);
        localStorage.setItem('userRole', data.role);

        // Call callback to update parent component state
        if (onLoginSuccess) {
          onLoginSuccess(data.role);
        } else {
          // Fallback: reload page if no callback
          window.location.reload();
        }
      } else if (data.ok && data.role !== 'admin') {
        setError('This account does not have admin access');
      } else {
        setError(data.message || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error(err);
    } finally {
      setIsLoggingIn(false);
    }
  }

  // Load users when consultants/companies/sites tabs are active
  useEffect(() => {
    if (['consultants', 'companies', 'sites'].includes(activeTab)) {
      loadUsers();
    } else if (activeTab === 'community') {
      loadCommunityPosts();
    }
  }, [activeTab]);

  // Filter users by role
  useEffect(() => {
    const roleMap: Record<TabType, string> = {
      consultants: 'professional',
      companies: 'company',
      sites: 'site',
      community: '',
    };

    const role = roleMap[activeTab];
    if (!role) return;

    let filtered = users.filter((u) => u.role === role);
    if (searchTerm) {
      filtered = filtered.filter(
        (u) =>
          u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.profile?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredUsers(filtered);
  }, [users, searchTerm, activeTab]);

  async function loadUsers() {
    setLoadingUsers(true);
    try {
      const res = await fetch(`${API_BASE}/users/all`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setUsers(data.users || []);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoadingUsers(false);
    }
  }

  async function loadCommunityPosts() {
    setLoadingPosts(true);
    try {
      const res = await fetch(`${API_BASE}/community/all`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPosts(data.posts || []);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoadingPosts(false);
    }
  }

  async function approveUser(userId: string) {
    const user = users.find((u) => u.id === userId);
    if (user) {
      setEmailPromptUser(user);
      setEmailPromptType('approve');
      setEmailTemplate(`Dear ${user.profile?.name || user.email},

Your profile has been reviewed and your account is now APPROVED. 

Your account has limited access to the platform at this stage:
- You can browse available opportunities and certifications
- You cannot view personal information of other platform members (data is anonymized)
- Full access will be granted once your verification is completed

Your verification will be completed once criminal background checks have been conducted by our team.

Best regards,
Clinical Trial Expertise (CTE) Platform Admin`);
      setShowEmailPrompt(true);
    }
  }

  async function verifyUser(userId: string) {
    const user = users.find((u) => u.id === userId);
    if (user) {
      setEmailPromptUser(user);
      setEmailPromptType('verify');
      setEmailTemplate(`Dear ${user.profile?.name || user.email},

Congratulations! Your criminal background checks have been completed and your account is now FULLY VERIFIED.

You now have unrestricted access to all platform features:
- View full profiles and contact information of other professionals
- Access all opportunities and certifications
- Engage fully with all platform resources

Thank you for choosing Clinical Trial Expertise (CTE).

Best regards,
Clinical Trial Expertise (CTE) Platform Admin`);
      setShowEmailPrompt(true);
    }
  }

  const canVerifyUser = (user: User) => {
    if (user.approvalStatus === 'verified' || user.verificationDate) {
      return false;
    }

    return user.approvalStatus === 'approved';
  };

  async function confirmEmailAndApprove() {
    if (!emailPromptUser) return;
    try {
      const authToken = localStorage.getItem('authToken');

      // Send approval/verification email via admin reply endpoint
      const emailRes = await fetch(`${API_BASE}/send-reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          to: emailPromptUser.email,
          subject: emailPromptType === 'approve' ? 'Profile Approved' : 'Profile Verified',
          message: emailTemplate,
          type: 'user',
          recipientName: emailPromptUser.profile?.name || emailPromptUser.email,
          itemId: emailPromptUser.id,
        }),
      });

      let emailStatusMessage = 'Email sent successfully.';
      if (emailRes.ok) {
        const emailData = await emailRes.json();
        if (emailData?.message?.toLowerCase().includes('not configured')) {
          emailStatusMessage = 'Email service is not configured, so no email was sent.';
        }
      } else {
        emailStatusMessage = `Email failed to send (HTTP ${emailRes.status}).`;
      }

      // Update approval status
      const approveRes = await fetch(
        `${API_BASE}/users/${emailPromptUser.id}/${emailPromptType === 'approve' ? 'approve' : 'verify'}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!approveRes.ok) {
        const errorData = await approveRes.text();
        throw new Error(`API Error: ${approveRes.status} - ${errorData}`);
      }

      const result = await approveRes.json();
      console.log('Approval response:', result);

      if (result?.user?.id) {
        setUsers((previousUsers) =>
          previousUsers.map((existingUser) =>
            existingUser.id === result.user.id ? { ...existingUser, ...result.user } : existingUser
          )
        );
      }

      setMessage(
        `${emailPromptType === 'approve' ? 'User approved and pending final verification checks.' : 'User fully verified after checks.'} ${emailStatusMessage}`
      );

      // Immediately reload users to refresh the UI with updated status
      console.log('[Admin] Calling loadUsers to refresh after approval');
      await loadUsers();

      setShowEmailPrompt(false);
    } catch (e) {
      console.error('Error in confirmEmailAndApprove:', e);
      setError((e as Error).message);
    }
  }

  async function suspendUser(userId: string) {
    try {
      const res = await fetch(`${API_BASE}/users/${userId}/suspend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMessage('User suspended');
      loadUsers();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function deletePost(postId: string) {
    try {
      const res = await fetch(`${API_BASE}/community/${postId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMessage('Post deleted');
      loadCommunityPosts();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function deleteComment(postId: string, commentId: string) {
    try {
      const res = await fetch(`${API_BASE}/community/${postId}/comments/${commentId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMessage('Comment deleted');
      loadCommunityPosts();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  if (!isAuthorized) {
    return (
      <section
        className="admin"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            maxWidth: '400px',
            width: '100%',
            padding: '2rem',
            border: '1px solid #ddd',
            borderRadius: '8px',
          }}
        >
          <h1>Admin Login</h1>
          <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            Enter your admin credentials to access the admin panel
          </p>

          <form onSubmit={handleAdminLogin}>
            <div style={{ marginBottom: '1rem' }}>
              <label
                htmlFor="adminEmail"
                style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}
              >
                Email Address
              </label>
              <input
                id="adminEmail"
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="your-email@example.com"
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label
                htmlFor="adminPassword"
                style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}
              >
                Password
              </label>
              <input
                id="adminPassword"
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="Your password"
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {error && (
              <div
                style={{
                  background: '#f8d7da',
                  color: '#721c24',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  marginBottom: '1rem',
                  fontSize: '0.9rem',
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoggingIn}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: '#0066cc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '1rem',
                fontWeight: '500',
                cursor: isLoggingIn ? 'not-allowed' : 'pointer',
                opacity: isLoggingIn ? 0.6 : 1,
              }}
            >
              {isLoggingIn ? 'Logging in...' : 'Login to Admin Panel'}
            </button>
          </form>
        </div>
      </section>
    );
  }

  return (
    <section className="admin">
      <div className="container" style={{ padding: '2rem 0' }}>
        <h1>Admin Dashboard</h1>

        {message && (
          <div
            style={{
              background: '#d4edda',
              color: '#155724',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
            }}
          >
            {message}
          </div>
        )}
        {error && (
          <div
            style={{
              background: '#f8d7da',
              color: '#721c24',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
            }}
          >
            {error}
          </div>
        )}

        <div
          className="admin-tabs"
          style={{
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '2rem',
            borderBottom: '1px solid #ddd',
          }}
        >
          {(['consultants', 'companies', 'sites', 'community'] as const).map((tab) => (
            <button
              key={tab}
              className={`tab-button ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                background: activeTab === tab ? '#0066cc' : '#f0f0f0',
                color: activeTab === tab ? 'white' : 'black',
                cursor: 'pointer',
                borderRadius: '4px 4px 0 0',
                fontSize: '1rem',
              }}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Consultants Tab */}
        {activeTab === 'consultants' && (
          <div>
            <h2>Consultants Management</h2>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                marginBottom: '1rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
              }}
            />
            {loadingUsers ? (
              <p>Loading...</p>
            ) : (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {filteredUsers.length === 0 ? (
                  <p>No consultants found</p>
                ) : (
                  filteredUsers.map((user) => (
                    <div
                      key={user.id}
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        padding: '1rem',
                        background: '#f9f9f9',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'start',
                        }}
                      >
                        <div>
                          <h3 style={{ margin: '0 0 0.5rem 0' }}>
                            {user.profile?.name || 'Unknown'}{' '}
                            <span style={{ fontSize: '0.8rem', color: '#666' }}>
                              ({user.approvalStatus || 'pending'})
                            </span>
                          </h3>
                          <p style={{ margin: '0.25rem 0' }}>
                            <strong>Email:</strong> {user.email}
                          </p>
                          {user.phone && (
                            <p style={{ margin: '0.25rem 0' }}>
                              <strong>Phone:</strong> {user.phone}
                            </p>
                          )}
                          <p style={{ margin: '0.25rem 0' }}>
                            <strong>Status:</strong> {user.status}
                          </p>
                          <p style={{ margin: '0.25rem 0' }}>
                            <strong>Approval Stage:</strong>{' '}
                            {user.approvalStatus === 'verified'
                              ? '✅ Fully Verified (Criminal Records Checked)'
                              : user.approvalStatus === 'approved'
                                ? '⏳ Pending Verification (Awaiting Criminal Record Checks)'
                                : '⏹️ Not Yet Approved'}
                          </p>
                          {user.approvalDate && (
                            <p style={{ margin: '0.25rem 0' }}>
                              <strong>Approved:</strong>{' '}
                              {new Date(user.approvalDate).toLocaleDateString()}
                            </p>
                          )}
                          {user.verificationDate && (
                            <p style={{ margin: '0.25rem 0' }}>
                              <strong>Verified:</strong>{' '}
                              {new Date(user.verificationDate).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            gap: '0.5rem',
                            flexDirection: 'row',
                            flexWrap: 'wrap',
                          }}
                        >
                          <button
                            onClick={() => approveUser(user.id)}
                            disabled={
                              user.approvalStatus === 'approved' ||
                              user.approvalStatus === 'verified'
                            }
                            style={{
                              padding: '0.5rem 1rem',
                              background:
                                user.approvalStatus === 'approved' ||
                                user.approvalStatus === 'verified'
                                  ? '#cccccc'
                                  : '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor:
                                user.approvalStatus === 'approved' ||
                                user.approvalStatus === 'verified'
                                  ? 'not-allowed'
                                  : 'pointer',
                              flex: '1 1 auto',
                              minWidth: '150px',
                              opacity:
                                user.approvalStatus === 'approved' ||
                                user.approvalStatus === 'verified'
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            Approve (Pending Checks)
                          </button>
                          <button
                            onClick={() => verifyUser(user.id)}
                            disabled={!canVerifyUser(user)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: !canVerifyUser(user) ? '#cccccc' : '#0066cc',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: !canVerifyUser(user) ? 'not-allowed' : 'pointer',
                              flex: '1 1 auto',
                              minWidth: '150px',
                              opacity: !canVerifyUser(user) ? 0.6 : 1,
                            }}
                          >
                            Verify (After Checks)
                          </button>
                          {user.status !== 'suspended' && (
                            <button
                              onClick={() => suspendUser(user.id)}
                              style={{
                                padding: '0.5rem 1rem',
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                flex: '1 1 auto',
                                minWidth: '150px',
                              }}
                            >
                              Suspend
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* Companies Tab */}
        {activeTab === 'companies' && (
          <div>
            <h2>Companies Management</h2>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                marginBottom: '1rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
              }}
            />
            {loadingUsers ? (
              <p>Loading...</p>
            ) : (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {filteredUsers.length === 0 ? (
                  <p>No companies found</p>
                ) : (
                  filteredUsers.map((user) => (
                    <div
                      key={user.id}
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        padding: '1rem',
                        background: '#f9f9f9',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'start',
                        }}
                      >
                        <div>
                          <h3 style={{ margin: '0 0 0.5rem 0' }}>
                            {user.profile?.name || 'Unknown'}{' '}
                            <span style={{ fontSize: '0.8rem', color: '#666' }}>
                              ({user.approvalStatus || 'pending'})
                            </span>
                          </h3>
                          <p style={{ margin: '0.25rem 0' }}>
                            <strong>Email:</strong> {user.email}
                          </p>
                          {user.phone && (
                            <p style={{ margin: '0.25rem 0' }}>
                              <strong>Phone:</strong> {user.phone}
                            </p>
                          )}
                          <p style={{ margin: '0.25rem 0' }}>
                            <strong>Status:</strong> {user.status}
                          </p>
                          <p style={{ margin: '0.25rem 0' }}>
                            <strong>Approval Stage:</strong>{' '}
                            {user.approvalStatus === 'verified'
                              ? '✅ Fully Verified (Criminal Records Checked)'
                              : user.approvalStatus === 'approved'
                                ? '⏳ Pending Verification (Awaiting Criminal Record Checks)'
                                : '⏹️ Not Yet Approved'}
                          </p>
                          {user.approvalDate && (
                            <p style={{ margin: '0.25rem 0' }}>
                              <strong>Approved:</strong>{' '}
                              {new Date(user.approvalDate).toLocaleDateString()}
                            </p>
                          )}
                          {user.verificationDate && (
                            <p style={{ margin: '0.25rem 0' }}>
                              <strong>Verified:</strong>{' '}
                              {new Date(user.verificationDate).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            gap: '0.5rem',
                            flexDirection: 'row',
                            flexWrap: 'wrap',
                          }}
                        >
                          <button
                            onClick={() => approveUser(user.id)}
                            disabled={
                              user.approvalStatus === 'approved' ||
                              user.approvalStatus === 'verified'
                            }
                            style={{
                              padding: '0.5rem 1rem',
                              background:
                                user.approvalStatus === 'approved' ||
                                user.approvalStatus === 'verified'
                                  ? '#cccccc'
                                  : '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor:
                                user.approvalStatus === 'approved' ||
                                user.approvalStatus === 'verified'
                                  ? 'not-allowed'
                                  : 'pointer',
                              flex: '1 1 auto',
                              minWidth: '150px',
                              opacity:
                                user.approvalStatus === 'approved' ||
                                user.approvalStatus === 'verified'
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => verifyUser(user.id)}
                            disabled={!canVerifyUser(user)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: !canVerifyUser(user) ? '#cccccc' : '#0066cc',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: !canVerifyUser(user) ? 'not-allowed' : 'pointer',
                              flex: '1 1 auto',
                              minWidth: '150px',
                              opacity: !canVerifyUser(user) ? 0.6 : 1,
                            }}
                          >
                            Verify
                          </button>
                          {user.status !== 'suspended' && (
                            <button
                              onClick={() => suspendUser(user.id)}
                              style={{
                                padding: '0.5rem 1rem',
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                flex: '1 1 auto',
                                minWidth: '150px',
                              }}
                            >
                              Suspend
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* Sites Tab */}
        {activeTab === 'sites' && (
          <div>
            <h2>Sites Management</h2>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                marginBottom: '1rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
              }}
            />
            {loadingUsers ? (
              <p>Loading...</p>
            ) : (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {filteredUsers.length === 0 ? (
                  <p>No sites found</p>
                ) : (
                  filteredUsers.map((user) => (
                    <div
                      key={user.id}
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        padding: '1rem',
                        background: '#f9f9f9',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'start',
                        }}
                      >
                        <div>
                          <h3 style={{ margin: '0 0 0.5rem 0' }}>
                            {user.profile?.name || 'Unknown'}{' '}
                            <span style={{ fontSize: '0.8rem', color: '#666' }}>
                              ({user.approvalStatus || 'pending'})
                            </span>
                          </h3>
                          <p style={{ margin: '0.25rem 0' }}>
                            <strong>Email:</strong> {user.email}
                          </p>
                          {user.phone && (
                            <p style={{ margin: '0.25rem 0' }}>
                              <strong>Phone:</strong> {user.phone}
                            </p>
                          )}
                          <p style={{ margin: '0.25rem 0' }}>
                            <strong>Status:</strong> {user.status}
                          </p>
                          <p style={{ margin: '0.25rem 0' }}>
                            <strong>Approval Stage:</strong>{' '}
                            {user.approvalStatus === 'verified'
                              ? '✅ Fully Verified (Criminal Records Checked)'
                              : user.approvalStatus === 'approved'
                                ? '⏳ Pending Verification (Awaiting Criminal Record Checks)'
                                : '⏹️ Not Yet Approved'}
                          </p>
                          {user.approvalDate && (
                            <p style={{ margin: '0.25rem 0' }}>
                              <strong>Approved:</strong>{' '}
                              {new Date(user.approvalDate).toLocaleDateString()}
                            </p>
                          )}
                          {user.verificationDate && (
                            <p style={{ margin: '0.25rem 0' }}>
                              <strong>Verified:</strong>{' '}
                              {new Date(user.verificationDate).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            gap: '0.5rem',
                            flexDirection: 'row',
                            flexWrap: 'wrap',
                          }}
                        >
                          <button
                            onClick={() => approveUser(user.id)}
                            disabled={
                              user.approvalStatus === 'approved' ||
                              user.approvalStatus === 'verified'
                            }
                            style={{
                              padding: '0.5rem 1rem',
                              background:
                                user.approvalStatus === 'approved' ||
                                user.approvalStatus === 'verified'
                                  ? '#cccccc'
                                  : '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor:
                                user.approvalStatus === 'approved' ||
                                user.approvalStatus === 'verified'
                                  ? 'not-allowed'
                                  : 'pointer',
                              flex: '1 1 auto',
                              minWidth: '150px',
                              opacity:
                                user.approvalStatus === 'approved' ||
                                user.approvalStatus === 'verified'
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => verifyUser(user.id)}
                            disabled={!canVerifyUser(user)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: !canVerifyUser(user) ? '#cccccc' : '#0066cc',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: !canVerifyUser(user) ? 'not-allowed' : 'pointer',
                              flex: '1 1 auto',
                              minWidth: '150px',
                              opacity: !canVerifyUser(user) ? 0.6 : 1,
                            }}
                          >
                            Verify
                          </button>
                          {user.status !== 'suspended' && (
                            <button
                              onClick={() => suspendUser(user.id)}
                              style={{
                                padding: '0.5rem 1rem',
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                flex: '1 1 auto',
                                minWidth: '150px',
                              }}
                            >
                              Suspend
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* Community Tab */}
        {activeTab === 'community' && (
          <div>
            <h2>Community Moderation</h2>
            {loadingPosts ? (
              <p>Loading...</p>
            ) : (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {posts.length === 0 ? (
                  <p>No community posts</p>
                ) : (
                  posts.map((post) => (
                    <div
                      key={post.id}
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        padding: '1rem',
                        background: '#f9f9f9',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'start',
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 0.5rem 0' }}>By: {post.userName}</h4>
                          <p style={{ margin: '0.25rem 0' }}>{post.content}</p>
                          {post.image && (
                            <img
                              src={post.image}
                              alt="Post content"
                              style={{
                                maxWidth: '200px',
                                marginTop: '0.5rem',
                                borderRadius: '4px',
                              }}
                            />
                          )}
                          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: '#666' }}>
                            {new Date(post.createdAt).toLocaleString()}
                          </p>
                          {post.comments.length > 0 && (
                            <div
                              style={{
                                marginTop: '1rem',
                                paddingTop: '1rem',
                                borderTop: '1px solid #ddd',
                              }}
                            >
                              <h5 style={{ margin: '0 0 0.5rem 0' }}>
                                Comments ({post.comments.length})
                              </h5>
                              {post.comments.map((comment) => (
                                <div
                                  key={comment.id}
                                  style={{
                                    marginBottom: '0.5rem',
                                    padding: '0.5rem',
                                    background: '#fff',
                                    borderRadius: '4px',
                                    fontSize: '0.9rem',
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <strong>{comment.userName}</strong>
                                    <button
                                      onClick={() => deleteComment(post.id, comment.id)}
                                      style={{
                                        padding: '0.25rem 0.5rem',
                                        background: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '2px',
                                        cursor: 'pointer',
                                        fontSize: '0.85rem',
                                      }}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                  <p style={{ margin: '0.25rem 0' }}>{comment.content}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => deletePost(post.id)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginLeft: '1rem',
                          }}
                        >
                          Delete Post
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* Email Prompt Modal */}
        {showEmailPrompt && emailPromptUser && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
            }}
          >
            <div
              style={{
                background: 'white',
                padding: '30px',
                borderRadius: '8px',
                maxWidth: '600px',
                width: '90%',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              }}
            >
              <h2>Send {emailPromptType === 'approve' ? 'Approval' : 'Verification'} Email</h2>
              <p>
                <strong>To:</strong> {emailPromptUser.email}
              </p>
              <p>
                <strong>User:</strong> {emailPromptUser.profile?.name || 'Unknown'}
              </p>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Email Message:
                </label>
                <textarea
                  value={emailTemplate}
                  onChange={(e) => setEmailTemplate(e.target.value)}
                  style={{
                    width: '100%',
                    height: '200px',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowEmailPrompt(false)}
                  style={{
                    padding: '10px 20px',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmEmailAndApprove}
                  style={{
                    padding: '10px 20px',
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Send & {emailPromptType === 'approve' ? 'Approve' : 'Verify'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
